import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogin, adminVerify } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Loader2, ArrowLeft, Waves } from 'lucide-react';
import { toast } from 'sonner';

import CenturionFooter from '@/components/CenturionFooter';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_magzine-maker/artifacts/c7i8lwjh_image.png";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await adminVerify();
        navigate('/admin/dashboard', { replace: true });
      } catch {
        setChecking(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('Please enter the admin password');
      return;
    }
    setLoading(true);
    try {
      const res = await adminLogin(password);
      localStorage.setItem('admin_token', res.data.token);
      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard', { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #004D6D 0%, #006994 30%, #00897B 60%, #40E0D0 100%)' }}>
        <div className="spinner" style={{ width: '40px', height: '40px', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #004D6D 0%, #006994 30%, #00897B 60%, #40E0D0 100%)' }}>
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[15%] left-[10%] w-40 h-40 rounded-full bg-[#FFB347]/10 blur-3xl" />
        <div className="absolute bottom-[20%] right-[10%] w-48 h-48 rounded-full bg-[#FC8EAC]/10 blur-3xl" />
        <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ height: '80px' }}>
          <path fill="rgba(255,255,255,0.1)" d="M0,80 C360,30 540,100 720,60 C900,20 1080,90 1440,50 L1440,120 L0,120 Z" />
        </svg>
      </div>

      <div className="relative z-10 w-full max-w-sm mx-4">
        {/* Back button */}
        <button
          data-testid="back-to-home"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to magazines</span>
        </button>

        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/30">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Treasure Cove" className="h-16 object-contain mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-[#0A192F] mb-1">Admin Access</h1>
            <p className="text-sm text-[#4A5568]">Enter the admin password to manage magazines</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" />
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="input-bahamian pl-10"
                  autoFocus
                />
              </div>
            </div>
            <Button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="btn-coral w-full"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Signing in...</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" />Sign In</>
              )}
            </Button>
          </form>
        </div>
      </div>
      <CenturionFooter />
    </div>
  );
};

export default AdminLogin;
